// Rendus de données de la page Statistiques : anneau, courbe, donut, barres. SVG écrit à la main, sans bibliothèque,
// jetons seulement (var(--…)) : le jour et la nuit suivent. Chaque dessin porte role="img" et un aria-label qui dit
// la donnée ; les textes (axes, dates, valeurs, légendes) vivent en HTML à la taille du système, jamais dans le SVG,
// qu'un viewBox mis à l'échelle rapetisserait. La couleur ne parle jamais seule : la valeur est toujours écrite.
import { html } from '../../core/html.js';

// 12.345 → « 12,3 » : la virgule décimale française, partout où un nombre à virgule s'écrit.
export function virgule(n, dec) { return Number(n || 0).toFixed(dec == null ? 1 : dec).replace('.', ','); }

// Anneau : une grandeur contre 100 %. Une pure marque de donnée, toujours de la même teinte (--jauge) : le verdict
// se lit sur son aimant, jamais sur la couleur du trait (un anneau qui n'encoderait le verdict qu'à moitié ferait
// croire à un code). Le trait part vide ; window.animerAnneaux (double rAF, à la première arrivée sur la page) pose
// data-offset sur stroke-dashoffset, et le pose tout de suite aux redessins. Les classes .anneau-val[data-offset]
// sont ce contrat, partagé avec le moteur.
export function anneau(pct, o) {
    o = o || {};
    var taille = o.taille || 64, ep = o.ep || 6;
    var p = Math.max(0, Math.min(100, Math.round(pct || 0)));
    var r = (taille - ep) / 2, C = 2 * Math.PI * r, cible = C * (1 - p / 100), c = taille / 2;
    return html`<div class="stats-anneau" style="--taille:${taille}px" role="img" aria-label="${o.titre || (p + ' %')}">
        <svg width="${taille}" height="${taille}" viewBox="0 0 ${taille} ${taille}" aria-hidden="true" focusable="false">
            <circle class="anneau-piste" cx="${c}" cy="${c}" r="${r}" fill="none" stroke-width="${ep}"/>
            <circle class="anneau-val" cx="${c}" cy="${c}" r="${r}" fill="none" stroke-width="${ep}" stroke-dasharray="${C.toFixed(2)}" stroke-dashoffset="${C.toFixed(2)}" data-offset="${cible.toFixed(2)}"/>
        </svg>
        <span class="stats-anneau-centre num" aria-hidden="true">${p}<span class="stats-anneau-pct"> %</span></span>
    </div>`;
}

// Courbe d'évolution : une seule série, donc pas de légende, le titre dit ce qui est tracé. Aire à 12 %, trait de
// 2 px (non mis à l'échelle), point de fin posé en HTML pour rester rond quel que soit l'écran. Le SVG ne porte que
// le tracé : maximum, zéro, dates et valeur finale sont autour, en 13 px tabulaires.
// Le viewBox garde un rapport fixe (420 × 120) et la feuille lui donne le même en aspect-ratio : la mise à l'échelle
// est uniforme, donc la pente d'une série est la même à 1280 qu'à 390 — un rythme se compare d'un écran à l'autre.
// points : [{ v, libelle, court, texte }]
export function courbe(points, o) {
    o = o || {};
    var n = points.length;
    if (n < 2) return html`<p class="stats-note">Pas encore assez de jours pour tracer une courbe.</p>`;
    var L = 420, H = 120, mg = { g: 4, d: 4, h: 8, b: 8 };
    var max = Math.max.apply(null, points.map(function(p) { return p.v; }).concat([1]));
    var x = function(i) { return mg.g + i * (L - mg.g - mg.d) / (n - 1); };
    var y = function(v) { return H - mg.b - (v / max) * (H - mg.h - mg.b); };
    var d = points.map(function(p, i) { return (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(p.v).toFixed(1); }).join(' ');
    var aire = d + ' L' + x(n - 1).toFixed(1) + ' ' + (H - mg.b) + ' L' + x(0).toFixed(1) + ' ' + (H - mg.b) + ' Z';
    var demi = (L - mg.g - mg.d) / (n - 1) / 2;
    var dernier = points[n - 1];
    var titre = (o.titre || 'Évolution') + ' : de ' + points[0].libelle + ' à ' + dernier.libelle + ', valeur finale ' + dernier.v + ', maximum ' + max;
    return html`<figure class="stats-courbe">
        ${o.titre ? html`<figcaption class="stats-courbe-titre">${o.titre}</figcaption>` : ''}
        <div class="stats-courbe-axe num" aria-hidden="true"><span>${max}</span><span>0</span></div>
        <div class="stats-courbe-trace">
            <svg class="stats-courbe-svg" viewBox="0 0 ${L} ${H}" role="img" aria-label="${titre}">
                <line class="stats-grille" x1="${mg.g}" y1="${H - mg.b}" x2="${L - mg.d}" y2="${H - mg.b}" vector-effect="non-scaling-stroke"/>
                <line class="stats-grille" x1="${mg.g}" y1="${y(max).toFixed(1)}" x2="${L - mg.d}" y2="${y(max).toFixed(1)}" vector-effect="non-scaling-stroke"/>
                <path class="stats-aire" d="${aire}"/>
                <path class="stats-trait" d="${d}" vector-effect="non-scaling-stroke"/>
                ${points.map(function(p, i) { return html`<rect x="${(x(i) - demi).toFixed(1)}" y="${mg.h}" width="${(demi * 2).toFixed(1)}" height="${H - mg.h - mg.b}" fill="transparent"><title>${p.libelle} : ${p.texte}</title></rect>`; })}
            </svg>
            <span class="stats-courbe-point" style="left:${(x(n - 1) / L * 100).toFixed(2)}%;top:${(y(dernier.v) / H * 100).toFixed(2)}%" aria-hidden="true"></span>
        </div>
        <div class="stats-courbe-pied num" aria-hidden="true"><span>${points[0].court}</span><span class="stats-courbe-fin">${dernier.court} · <strong>${dernier.v}</strong></span></div>
    </figure>`;
}

// Donut : des parts d'un tout, au plus quatre ou cinq, toujours avec sa légende (nom, valeur écrite, part en %)
// posée juste à côté. Les parts vides ne sont pas dessinées mais restent dans la légende, à zéro.
// parts : [{ nom, valeur, texte, couleur }] — couleur : un jeton, var(--…).
export function donut(parts, o) {
    o = o || {};
    var taille = o.taille || 140, ep = o.ep || 18, c = taille / 2;
    var pleines = parts.filter(function(x) { return x.valeur > 0; });
    var total = pleines.reduce(function(a, x) { return a + x.valeur; }, 0);
    var r = (taille - ep) / 2, C = 2 * Math.PI * r, cumul = 0;
    var gap = pleines.length > 1 ? 2.5 : 0;   // 2,5 px de jour entre les parts
    var segments = pleines.map(function(x) {
        var seg = C * x.valeur / total, plein = Math.max(0, seg - gap), depart = cumul;
        cumul += seg;
        return html`<circle class="stats-donut-part" cx="${c}" cy="${c}" r="${r}" fill="none" style="stroke:${x.couleur}" stroke-width="${ep}" stroke-dasharray="${plein.toFixed(2)} ${(C - plein).toFixed(2)}" stroke-dashoffset="${(-depart).toFixed(2)}"><title>${x.nom} : ${x.texte || x.valeur} (${Math.round(x.valeur / total * 100)} %)</title></circle>`;
    });
    var resume = parts.map(function(x) { return x.nom + ' ' + (x.texte || x.valeur) + (total && x.valeur ? ' (' + Math.round(x.valeur / total * 100) + ' %)' : ''); }).join(', ');
    return html`<div class="stats-donut">
        <svg class="stats-donut-svg" width="${taille}" height="${taille}" viewBox="0 0 ${taille} ${taille}" role="img" aria-label="${(o.titre || 'Répartition') + ' : ' + resume}">
            <circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="var(--track)" stroke-width="${ep}"/>
            ${segments}
        </svg>
        <ul class="stats-legende">${parts.map(function(x) {
            var pct = total ? Math.round(x.valeur / total * 100) : 0;
            return html`<li><span class="stats-legende-point" style="background:${x.couleur}" aria-hidden="true"></span><span class="stats-legende-nom">${x.nom}</span><span class="stats-legende-val num">${x.texte || String(x.valeur)}${x.valeur ? html` <span class="stats-legende-pct">· ${pct} %</span>` : ''}</span></li>`;
        })}</ul>
    </div>`;
}

// Barres horizontales : comparer des grandeurs. En HTML (liste), pas en SVG : les libellés gardent leur taille et
// chaque ligne se lit (nom, valeur) sans dépendre de la barre, qui n'est qu'un dessin.
// lignes : [{ nom, valeur, texte, detail, titre }] — o.max : l'échelle, quand les valeurs sont des parts d'un tout
// (o.max = 100 pour des pourcentages) ; sans elle la piste est normalisée au maximum de la liste, et une barre
// pleine ne voudrait plus dire « tout », ce que le chiffre à droite démentirait.
export function barres(lignes, o) {
    o = o || {};
    if (!lignes.length) return html`<p class="stats-note">${o.vide || 'Rien à afficher pour l\'instant.'}</p>`;
    var ech = o.max || Math.max.apply(null, lignes.map(function(l) { return l.valeur; }).concat([1]));
    return html`<ul class="stats-barres"${o.titre ? html` aria-label="${o.titre}"` : ''}>${lignes.map(function(l) {
        return html`<li class="stats-barre"${l.titre ? html` title="${l.titre}"` : ''}>
            <span class="stats-barre-nom">${l.nom}</span>
            <span class="stats-barre-piste jauge-piste" aria-hidden="true"><span class="jauge-val" style="--p:${Math.max(0.01, Math.min(1, l.valeur / ech)).toFixed(3)}"></span></span>
            <span class="stats-barre-val num">${l.texte || String(l.valeur)}${l.detail ? html` <span class="stats-barre-detail">${l.detail}</span>` : ''}</span>
        </li>`;
    })}</ul>`;
}
